/*
 * NotFoundPage
 * 404 screen when a tour or page cannot be found
 */
import React, { memo, useCallback, useEffect, useState } from 'react';
import { NextSeo } from 'next-seo';
import { useGetTenant } from '@containers/Home/hook/useGetTenant';

const ASSET_PREFIX = process.env.NEXT_PUBLIC_SUBFOLDER || '';
const LOGO_SRC = `${process.env.NEXT_PUBLIC_ASSETS_URL}/static/logo.png`;

const ART_VARIANTS = [
  {
    src: `${ASSET_PREFIX}/images/404-otter.webp`,
    width: 1024,
    height: 682,
  },
  {
    src: `${ASSET_PREFIX}/images/404-fox.webp`,
    width: 1024,
    height: 768,
  },
  {
    src: `${ASSET_PREFIX}/images/404-rabbit.webp`,
    width: 1024,
    height: 768,
  },
  {
    src: `${ASSET_PREFIX}/images/404-cat.webp`,
    width: 1024,
    height: 768,
  },
  {
    src: `${ASSET_PREFIX}/images/404-raccoon.webp`,
    width: 1024,
    height: 768,
  },
] as const;

const HomeArrow = (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="size-4">
    <path
      d="M3 8h10M9.5 4.5L13.5 8 9.5 11.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const pickArtVariant = () =>
  ART_VARIANTS[Math.floor(Math.random() * ART_VARIANTS.length)];

const NotFound = memo(() => {
  const { tenant, onGetTenantByDomain } = useGetTenant({ host: '' });
  const [art, setArt] = useState<(typeof ART_VARIANTS)[number]>();
  const [ready, setReady] = useState(false);

  const onGoBackDashboard = useCallback(
    () => window.open(tenant?.domain_studio, '_self'),
    [tenant?.domain_studio],
  );

  useEffect(() => {
    onGetTenantByDomain();
  }, [onGetTenantByDomain]);

  useEffect(() => {
    setArt(pickArtVariant());
    setReady(true);
  }, []);

  return (
    <>
      {tenant ? (
        <NextSeo
          title={tenant?.title}
          description={tenant?.description}
          additionalMetaTags={[
            {
              property: 'name',
              content: tenant?.name,
            },
            {
              name: 'application-name',
              content: tenant?.name,
            },
          ]}
          additionalLinkTags={[
            {
              rel: 'shortcut icon',
              href: tenant?.favicon?.src,
            },
          ]}
          openGraph={{
            type: 'website',
            url: tenant?.domain_public + '/404',
            title: tenant?.title,
            description: tenant?.description,
            images: [
              {
                url: tenant?.logo?.src,
                width: 800,
                height: 600,
                alt: tenant?.name,
              },
            ],
            site_name: tenant?.name,
          }}
        />
      ) : null}
      <div className="relative isolate flex h-full max-h-full w-full flex-col overflow-hidden overscroll-none">
        <div
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-black"
          aria-hidden="true"
        >
          {art ? (
            <div className="absolute right-0 bottom-0 left-0 w-full md:inset-0">
              <img
                src={art.src}
                alt=""
                width={art.width}
                height={art.height}
                decoding="async"
                fetchPriority="high"
                className="block h-auto w-full md:h-full md:object-cover md:object-bottom"
              />
              <div className="absolute inset-0 bg-gradient-to-b h-1/4 from-black to-transparent md:hidden" />
            </div>
          ) : null}
          <div className="absolute inset-0 hidden bg-gradient-to-r from-[#070b14] from-0% via-[#070b14]/85 via-[28%] to-transparent to-[70%] md:block" />
        </div>

        <button
          type="button"
          onClick={onGoBackDashboard}
          aria-label="Back to home"
          className="absolute top-5 left-1/2 -translate-x-1/2 md:left-5 md:translate-x-0 z-20 w-[min(168px,46vw)] cursor-pointer border-0 bg-transparent p-0 md:top-7 md:left-9"
        >
          <img
            src={LOGO_SRC}
            alt="Panoee"
            width={558}
            height={134}
            className="block h-auto w-full mix-blend-screen"
          />
        </button>

        <div
          className={`relative z-10 mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col items-center justify-start gap-8 overflow-hidden px-10 pt-[40%] text-center text-white transition-all duration-700 ease-out md:mx-0 md:justify-center md:pt-0 md:pr-0 md:pl-20 ${
            ready ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
          }`}
        >
          <h1 className="!text-5xl font-semibold text-balance">
            This tour is off the map
          </h1>
          <p className="text-[clamp(15px,1.6vw,18px)] text-balance">
            The tour you’re looking for may have moved or no longer exists.
          </p>
          <button
            type="button"
            onClick={onGoBackDashboard}
            className="inline-flex w-fit cursor-pointer items-center gap-2.5 rounded-full border-0 bg-gradient-to-r from-yellow-400 to-yellow-500 px-5 py-4 font-medium text-black transition-all duration-200 hover:brightness-105"
          >
            Back to home
            {HomeArrow}
          </button>
        </div>

        <p className="relative z-10 mb-4 text-center text-sm text-white/70 md:mb-[22px]">
          Powered by{' '}
          <a
            href="https://panoee.com"
            className="font-semibold text-white no-underline hover:underline"
          >
            Panoee
          </a>
        </p>
      </div>
    </>
  );
});

export default NotFound;
